import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSearch } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen,
  LayoutDashboard,
  ShoppingCart,
  ClipboardCheck,
  Factory,
  Database,
  ChevronRight,
  Workflow,
  Wrench,
  Package,
  Users,
  CheckCircle2,
  MessageSquarePlus,
  CreditCard,
  MousePointerClick,
  Zap,
  AlertTriangle,
  Printer,
  Link2,
  Shield,
  Settings,
  ClipboardList,
  Image as ImageIcon,
  Warehouse,
  ShieldCheck,
  Banknote,
  Search,
  FileText,
  Layers,
  X,
  ZoomIn,
  CheckSquare,
} from "lucide-react";
import { guideData } from "@/config/user-guide-data";
import { GuideModule, GuideScreenshot } from "@/shared/schemas/config.schema";
import mermaid from "mermaid";
import { emsTabClass } from "@/components/ui/ems-tabs";

export function formatGuideText(text?: string): React.ReactNode {
  if (!text) return null;
  const tokens = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return tokens.map((token, idx) => {
    if (token.startsWith("**") && token.endsWith("**") && token.length > 4) {
      const inner = token.slice(2, -2);
      if (inner.startsWith("`") && inner.endsWith("`") && inner.length > 2) {
        return (
          <code
            key={idx}
            className="px-1.5 py-0.5 mx-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-800 font-mono text-[11px] font-bold"
          >
            {inner.slice(1, -1)}
          </code>
        );
      }
      return (
        <strong key={idx} className="font-bold text-slate-900">
          {inner.replace(/`/g, "")}
        </strong>
      );
    }
    if (token.startsWith("`") && token.endsWith("`") && token.length > 2) {
      return (
        <code
          key={idx}
          className="px-1.5 py-0.5 mx-0.5 rounded bg-slate-100 border border-slate-200 text-indigo-700 font-mono text-[11px] font-semibold"
        >
          {token.slice(1, -1)}
        </code>
      );
    }
    return <React.Fragment key={idx}>{token}</React.Fragment>;
  });
}

export const iconMap: Record<string, React.ElementType> = {
  LayoutDashboard,
  ShoppingCart,
  ClipboardCheck,
  Factory,
  Database,
  BookOpen,
  Workflow,
  Wrench,
  Package,
  Users,
  CheckCircle2,
  MessageSquarePlus,
  CreditCard,
  Settings,
  ClipboardList,
  Image: ImageIcon,
  Warehouse,
  ShieldCheck,
  Banknote,
  FileText,
  Layers,
};

export function findModuleByKey(
  modules: GuideModule[],
  rawKey?: string,
): GuideModule | undefined {
  if (!rawKey) return undefined;
  const cleanKey = rawKey.trim().toLowerCase().replace(/^\/+|\/+$/g, "");
  // Khớp đúng mã/alias ở mọi chương trước, rồi mới khớp theo tiền tố (tránh "product" bắt nhầm "production-orders")
  return (
    modules.find(
      (m) => m.id.toLowerCase() === cleanKey || m.aliases?.some((a) => a.toLowerCase() === cleanKey),
    ) || modules.find((m) => m.aliases?.some((a) => cleanKey.startsWith(a.toLowerCase())))
  );
}

export function matchesGuideSearch(mod: GuideModule, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (mod.title.toLowerCase().includes(q)) return true;
  if (mod.purpose?.toLowerCase().includes(q)) return true;
  if (mod.isoRef?.toLowerCase().includes(q)) return true;
  if (mod.scope?.some((s) => s.toLowerCase().includes(q))) return true;
  if (
    mod.terms?.some(
      (t) =>
        t.term.toLowerCase().includes(q) ||
        t.definition.toLowerCase().includes(q),
    )
  )
    return true;
  if (
    mod.mainFlow?.some(
      (f) =>
        f.action.toLowerCase().includes(q) ||
        f.detail.toLowerCase().includes(q),
    )
  )
    return true;
  if (
    mod.roleCrudTestScenarios?.some(
      (r) =>
        r.roleGroup.toLowerCase().includes(q) ||
        r.createTest.toLowerCase().includes(q) ||
        r.readTest.toLowerCase().includes(q) ||
        r.updateTest.toLowerCase().includes(q) ||
        r.deleteTest.toLowerCase().includes(q) ||
        r.expectedResult.toLowerCase().includes(q),
    )
  )
    return true;
  if (
    mod.uatChecklist?.some(
      (c) =>
        c.id.toLowerCase().includes(q) ||
        c.item.toLowerCase().includes(q) ||
        c.standard.toLowerCase().includes(q) ||
        c.method.toLowerCase().includes(q),
    )
  )
    return true;
  if (mod.notes?.some((n) => n.toLowerCase().includes(q))) return true;
  return false;
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-8 break-inside-avoid-page">
      <h3 className="flex items-center gap-2 text-base font-bold text-slate-800 mb-3">
        {icon}
        {title}
      </h3>
      <div className="ml-0 md:ml-6">{children}</div>
    </section>
  );
}

export default function UserGuidePage() {
  const search = useSearch({ strict: false }) as {
    module?: string;
    view?: string;
  };
  const moduleKey = search?.module;
  const initialView = search?.view === "full" ? "full" : "single";
  const modules = guideData.modules;

  const [selectedModule, setSelectedModule] = useState<GuideModule>(
    () => findModuleByKey(modules, moduleKey) || modules[0],
  );
  const [viewMode, setViewMode] = useState<"single" | "full">(initialView);
  const [searchQuery, setSearchQuery] = useState("");
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const [zoomedScreenshot, setZoomedScreenshot] =
    useState<GuideScreenshot | null>(null);
  const scrollWrapperRef = useRef<HTMLDivElement>(null);

  const filteredModules = useMemo(
    () => modules.filter((m) => matchesGuideSearch(m, searchQuery)),
    [modules, searchQuery],
  );

  useEffect(() => {
    if (moduleKey) {
      const found = findModuleByKey(modules, moduleKey);
      if (found) {
        setSelectedModule(found);
      }
    }
  }, [moduleKey, modules]);

  useEffect(() => {
    if (search?.view === "full") {
      setViewMode("full");
    } else if (search?.view === "single") {
      setViewMode("single");
    }
  }, [search?.view]);

  useEffect(() => {
    if (scrollWrapperRef.current) {
      scrollWrapperRef.current.scrollTop = 0;
    }
  }, [selectedModule?.id, viewMode]);

  useEffect(() => {
    mermaid.initialize({ startOnLoad: false, theme: "default" });
    const timer = setTimeout(() => {
      // Chỉ vẽ sơ đồ đang hiển thị và chưa vẽ: Mermaid vẽ trong khung ẩn / chưa có kích thước sẽ lỗi
      // "Could not find a suitable point…" (thỉnh thoảng). Sơ đồ ẩn được vẽ khi người dùng mở tới.
      const nodes = Array.from(document.querySelectorAll<HTMLElement>(".mermaid")).filter(
        (el) => !el.hasAttribute("data-processed") && el.offsetWidth > 0 && el.offsetHeight > 0,
      );
      if (nodes.length === 0) return;
      mermaid.run({ nodes }).catch((err) => {
        // Sơ đồ lỗi vẫn giữ nội dung chữ; không coi là lỗi trang
        console.warn("[UserGuide] Không vẽ được sơ đồ:", err?.message || err);
      });
    }, 80);
    return () => clearTimeout(timer);
  }, [selectedModule, viewMode, filteredModules.length]);

  const toggleChecklist = (code: string) => {
    setCheckedItems((prev) => ({ ...prev, [code]: !prev[code] }));
  };

  const handlePrintFullManual = () => {
    if (viewMode !== "full") {
      setViewMode("full");
      setTimeout(() => {
        window.print();
      }, 500);
    } else {
      window.print();
    }
  };

  const handlePrintCurrentChapter = () => {
    if (viewMode !== "single") {
      setViewMode("single");
      setTimeout(() => {
        window.print();
      }, 300);
    } else {
      window.print();
    }
  };

  const renderModuleDoc = (mod: GuideModule) => {
    let n = 1;
    const sections: React.ReactNode[] = [];

    // 1. Mục đích & Phạm vi
    sections.push(
      <Section
        key="purpose"
        title={`${n}. Mục đích & Phạm vi áp dụng`}
        icon={<BookOpen size={16} className="text-indigo-600" />}
      >
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
          <div>
            <span className="font-semibold text-sm text-slate-800">
              Mục đích:
            </span>
            <p className="text-sm text-slate-700 mt-1 whitespace-pre-line leading-relaxed">
              {formatGuideText(mod.purpose)}
            </p>
          </div>
          {mod.scope && mod.scope.length > 0 && (
            <div>
              <span className="font-semibold text-sm text-slate-800">
                Phạm vi áp dụng:
              </span>
              <ul className="list-disc pl-5 mt-1 space-y-1">
                {mod.scope.map((s, i) => (
                  <li key={i} className="text-sm text-slate-700">
                    {formatGuideText(s)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Section>,
    );
    n++;

    // 2. Điều khoản tham chiếu
    if (mod.references && mod.references.length > 0) {
      sections.push(
        <Section
          key="references"
          title={`${n}. Tiêu chuẩn & Tài liệu tham chiếu`}
          icon={<BookOpen size={16} className="text-teal-600" />}
        >
          <ul className="list-disc pl-5 mt-1 space-y-1">
            {mod.references.map((r, i) => (
              <li key={i} className="text-sm text-slate-700">
                {formatGuideText(r)}
              </li>
            ))}
          </ul>
        </Section>,
      );
      n++;
    }

    // 3. Thuật ngữ & Định nghĩa
    if (mod.terms && mod.terms.length > 0) {
      sections.push(
        <Section
          key="terms"
          title={`${n}. Thuật ngữ & Định nghĩa chuẩn`}
          icon={<BookOpen size={16} className="text-purple-600" />}
        >
          <div className="grid gap-2">
            {mod.terms.map((t, i) => (
              <div
                key={i}
                className="bg-slate-50 p-3 rounded-lg border border-slate-200"
              >
                <span className="font-bold text-sm text-slate-900">
                  {t.term}:{" "}
                </span>
                <span className="text-sm text-slate-700">
                  {formatGuideText(t.definition)}
                </span>
              </div>
            ))}
          </div>
        </Section>,
      );
      n++;
    }

    // 4. Vai trò (Roles)
    if (mod.responsibilities && mod.responsibilities.length > 0) {
      sections.push(
        <Section
          key="roles"
          title={`${n}. Phân công trách nhiệm thực hiện`}
          icon={<Users size={16} className="text-blue-600" />}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {mod.responsibilities.map((r, i) => (
              <div
                key={i}
                className="border border-slate-200 rounded-lg p-3 bg-white shadow-xs"
              >
                <div className="font-bold text-sm text-blue-700 mb-2 border-b pb-1.5">
                  {r.role}
                </div>
                <ul className="list-disc pl-4 space-y-1">
                  {r.tasks.map((task, j) => (
                    <li
                      key={j}
                      className="text-xs text-slate-700 leading-relaxed"
                    >
                      {formatGuideText(task)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>,
      );
      n++;
    }

    // 5. Quy tắc phân quyền RBAC
    if (mod.permissionRules && mod.permissionRules.length > 0) {
      sections.push(
        <Section
          key="perms"
          title={`${n}. Ma trận Phân quyền Thao tác (RBAC)`}
          icon={<Shield size={16} className="text-rose-600" />}
        >
          <div className="overflow-x-auto border border-slate-200 rounded-lg shadow-xs">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-rose-50/80 border-b border-rose-100">
                  <th className="text-left px-3 py-2 font-bold text-slate-700">
                    Vai trò (Role)
                  </th>
                  <th className="text-center px-3 py-2 font-bold text-slate-700">
                    Xem
                  </th>
                  <th className="text-center px-3 py-2 font-bold text-slate-700">
                    Tạo
                  </th>
                  <th className="text-center px-3 py-2 font-bold text-slate-700">
                    Sửa
                  </th>
                  <th className="text-center px-3 py-2 font-bold text-slate-700">
                    Xóa
                  </th>
                  <th className="text-center px-3 py-2 font-bold text-slate-700">
                    Duyệt
                  </th>
                  <th className="text-center px-3 py-2 font-bold text-slate-700">
                    Xuất Excel/In
                  </th>
                </tr>
              </thead>
              <tbody>
                {mod.permissionRules.map((p, i) => (
                  <tr
                    key={i}
                    className="border-t border-slate-100 bg-white hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-3 py-2 font-semibold text-slate-800 text-xs">
                      {p.role}
                      {p.statusRules && (
                        <span className="block text-[11px] text-slate-500 font-normal mt-0.5">
                          {p.statusRules}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">{p.view}</td>
                    <td className="px-3 py-2 text-center">{p.create}</td>
                    <td className="px-3 py-2 text-center">{p.edit}</td>
                    <td className="px-3 py-2 text-center">{p.delete}</td>
                    <td className="px-3 py-2 text-center">{p.approve}</td>
                    <td className="px-3 py-2 text-center">{p.export}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-slate-600 mt-2 bg-slate-50 p-2 rounded border border-slate-200 flex flex-wrap gap-4">
            <span>✅ : Được phép thao tác</span>
            <span>👁 : Chỉ xem / dữ liệu phạm vi</span>
            <span>⚡ : Phụ thuộc trạng thái chứng từ</span>
            <span>❌ : Hệ thống khóa quyền</span>
          </div>
        </Section>,
      );
      n++;
    }

    // 6. Lưu đồ Quy trình (Process Flowchart)
    if (mod.processFlowchart) {
      sections.push(
        <Section
          key="flowchart"
          title={`${n}. Lưu đồ Quy trình Nghiệp vụ`}
          icon={<Workflow size={16} className="text-blue-600" />}
        >
          <div className="bg-white border border-slate-200 rounded-lg p-4 overflow-x-auto">
            <pre
              key={`${mod.id}-flowchart`}
              className="mermaid flex justify-center text-sm"
            >
              {mod.processFlowchart}
            </pre>
          </div>
        </Section>,
      );
      n++;
    }

    // 7. Luồng xử lý chính (Main Flow)
    if (mod.mainFlow && mod.mainFlow.length > 0) {
      sections.push(
        <Section
          key="mainflow"
          title={`${n}. Hướng dẫn Thao tác từng bước (Step-by-Step)`}
          icon={<Workflow size={16} className="text-emerald-600" />}
        >
          <div className="space-y-3">
            {mod.mainFlow.map((step, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-white shadow-xs hover:border-emerald-300 transition-colors"
              >
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-emerald-600 text-white font-bold text-xs shrink-0 mt-0.5">
                  {step.step}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-slate-900 text-sm mb-1">
                    {formatGuideText(step.action)}
                  </h4>
                  <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">
                    {formatGuideText(step.detail)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Section>,
      );
      n++;
    }

    // 8. Luồng phụ / Ngoại lệ (Sub Flows)
    if (mod.subFlows && mod.subFlows.length > 0) {
      sections.push(
        <Section
          key="subflows"
          title={`${n}. Tình huống đặc thù & Xử lý ngoại lệ`}
          icon={<AlertTriangle size={16} className="text-orange-600" />}
        >
          <div className="space-y-3">
            {mod.subFlows.map((sub, i) => (
              <div
                key={i}
                className="border border-orange-200 rounded-lg p-3.5 bg-orange-50/40"
              >
                <div className="font-bold text-sm text-orange-900 mb-2 flex items-center gap-2">
                  <Workflow size={14} className="text-orange-600" />
                  {sub.name}
                </div>
                <ul className="space-y-1.5">
                  {sub.steps.map((step, j) => (
                    <li
                      key={j}
                      className="text-xs text-slate-800 flex items-start gap-2 leading-relaxed"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-1.5 shrink-0"></span>
                      <span className="whitespace-pre-line">
                        {formatGuideText(step)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>,
      );
      n++;
    }

    // 9. Kịch bản Kiểm thử End-User theo Vai trò — CRUD (NEW!)
    if (mod.roleCrudTestScenarios && mod.roleCrudTestScenarios.length > 0) {
      sections.push(
        <Section
          key="role-crud-scenarios"
          title={`${n}. Kịch bản Kiểm thử End-User theo Vai trò (Test CRUD)`}
          icon={<ClipboardList size={16} className="text-indigo-600" />}
        >
          <div
            data-testid={`crud-scenarios-${mod.id}`}
            className="overflow-x-auto border border-indigo-200 rounded-lg shadow-xs"
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-indigo-50 border-b border-indigo-200">
                  <th className="text-left px-3 py-2.5 font-bold text-indigo-950 w-[16%]">
                    Nhóm Vai trò (Role)
                  </th>
                  <th className="text-left px-3 py-2.5 font-bold text-indigo-950 w-[17%]">
                    1. Thêm mới (Create)
                  </th>
                  <th className="text-left px-3 py-2.5 font-bold text-indigo-950 w-[16%]">
                    2. Xem & Tra cứu (Read)
                  </th>
                  <th className="text-left px-3 py-2.5 font-bold text-indigo-950 w-[17%]">
                    3. Cập nhật / Duyệt (Update)
                  </th>
                  <th className="text-left px-3 py-2.5 font-bold text-indigo-950 w-[16%]">
                    4. Xóa / Hủy (Delete)
                  </th>
                  <th className="text-left px-3 py-2.5 font-bold text-emerald-900 bg-emerald-50/70 w-[18%]">
                    Tiêu chuẩn Đạt (Expected Pass)
                  </th>
                </tr>
              </thead>
              <tbody>
                {mod.roleCrudTestScenarios.map((sc, i) => (
                  <tr
                    key={i}
                    className="border-t border-slate-200 bg-white hover:bg-indigo-50/20 align-top"
                  >
                    <td className="px-3 py-2.5 font-bold text-indigo-900 leading-relaxed">
                      {sc.roleGroup}
                    </td>
                    <td className="px-3 py-2.5 text-slate-700 leading-relaxed">
                      {sc.createTest}
                    </td>
                    <td className="px-3 py-2.5 text-slate-700 leading-relaxed">
                      {sc.readTest}
                    </td>
                    <td className="px-3 py-2.5 text-slate-700 leading-relaxed">
                      {sc.updateTest}
                    </td>
                    <td className="px-3 py-2.5 text-slate-700 leading-relaxed">
                      {sc.deleteTest}
                    </td>
                    <td className="px-3 py-2.5 font-semibold text-emerald-800 bg-emerald-50/30 leading-relaxed">
                      {sc.expectedResult}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>,
      );
      n++;
    }

    // 10. Checklist Nghiệm thu Go-Live & Tiêu chuẩn Đánh giá (NEW!)
    if (mod.uatChecklist && mod.uatChecklist.length > 0) {
      sections.push(
        <Section
          key="uat-checklist"
          title={`${n}. Checklist Nghiệm thu Go-Live & Tiêu chuẩn Đánh giá`}
          icon={<CheckSquare size={16} className="text-emerald-600" />}
        >
          <div
            data-testid={`uat-checklist-${mod.id}`}
            className="overflow-x-auto border border-emerald-200 rounded-lg shadow-xs"
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-emerald-50 border-b border-emerald-200">
                  <th className="text-center px-2.5 py-2.5 font-bold text-emerald-950 w-10">
                    Đạt
                  </th>
                  <th className="text-left px-3 py-2.5 font-bold text-emerald-950 w-24">
                    Mã KT
                  </th>
                  <th className="text-left px-3 py-2.5 font-bold text-emerald-950 w-[26%]">
                    Hạng mục Kiểm tra
                  </th>
                  <th className="text-left px-3 py-2.5 font-bold text-emerald-950 w-[34%]">
                    Tiêu chuẩn Đánh giá Đạt (Pass Criteria)
                  </th>
                  <th className="text-left px-3 py-2.5 font-bold text-emerald-950">
                    Cách thức Kiểm chứng trên App
                  </th>
                </tr>
              </thead>
              <tbody>
                {mod.uatChecklist.map((item, i) => {
                  const key = `${mod.id}-${item.id}`;
                  const isChecked = !!checkedItems[key];
                  return (
                    <tr
                      key={i}
                      onClick={() => toggleChecklist(key)}
                      className={`border-t border-slate-200 cursor-pointer transition-colors align-top ${
                        isChecked
                          ? "bg-emerald-50/50"
                          : "bg-white hover:bg-slate-50"
                      }`}
                    >
                      <td className="px-2.5 py-2.5 text-center">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {}}
                          className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2.5 font-mono font-bold text-emerald-800 whitespace-nowrap">
                        {item.id}
                      </td>
                      <td className="px-3 py-2.5 font-semibold text-slate-900 leading-relaxed">
                        {item.item}
                      </td>
                      <td className="px-3 py-2.5 text-slate-700 leading-relaxed">
                        {item.standard}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 leading-relaxed">
                        {item.method}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>,
      );
      n++;
    }

    // 11. Trạng thái (Status Transitions)
    if (mod.statusTransitions && mod.statusTransitions.length > 0) {
      sections.push(
        <Section
          key="transitions"
          title={`${n}. Vòng đời Chuyển đổi Trạng thái`}
          icon={<Zap size={16} className="text-amber-600" />}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {mod.statusTransitions.map((t, i) => (
              <div
                key={i}
                className="flex flex-col gap-1.5 border border-slate-200 p-3 rounded-lg bg-slate-50 shadow-xs"
              >
                <div className="flex items-center gap-2 text-xs flex-wrap">
                  <span className="bg-slate-200 px-2 py-1 rounded text-slate-800 font-semibold">
                    {t.from}
                  </span>
                  <span className="text-slate-400 font-bold">→</span>
                  <span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded font-semibold">
                    {t.to}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-1 text-[11px] gap-2">
                  <span className="text-slate-700 font-medium">
                    Thao tác: {t.action}
                  </span>
                  <span className="text-slate-600 bg-white border px-1.5 py-0.5 rounded text-[10px] font-medium">
                    {t.allowedRoles}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Section>,
      );
      n++;
    }

    // 12. Action Links (Kế thừa chứng từ Link-to-Form)
    if (mod.actionLinks && mod.actionLinks.length > 0) {
      sections.push(
        <Section
          key="actionlinks"
          title={`${n}. Liên kết Kế thừa Chứng từ (Link-to-Form)`}
          icon={<Link2 size={16} className="text-cyan-600" />}
        >
          <div className="overflow-x-auto border border-slate-200 rounded-lg shadow-xs">
            {/* Cột cố định: 2 cột đầu xuống dòng, cột Cơ chế (nhiều chữ nhất) rộng nhất */}
            <table className="w-full table-fixed text-sm">
              <colgroup>
                <col className="w-[20%]" />
                <col className="w-[20%]" />
                <col className="w-[18%]" />
                <col className="w-[42%]" />
              </colgroup>
              <thead>
                <tr className="bg-cyan-50/80 border-b border-cyan-100">
                  <th className="text-left px-3 py-2 font-bold text-slate-700">
                    Từ Màn hình
                  </th>
                  <th className="text-left px-3 py-2 font-bold text-slate-700">
                    → Đích đến
                  </th>
                  <th className="text-left px-3 py-2 font-bold text-slate-700">
                    Nút Thao tác
                  </th>
                  <th className="text-left px-3 py-2 font-bold text-slate-700">
                    Cơ chế Kế thừa Dữ liệu
                  </th>
                </tr>
              </thead>
              <tbody>
                {mod.actionLinks.map((a, i) => (
                  <tr
                    key={i}
                    className="border-t border-slate-100 bg-white hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-3 py-2 font-semibold text-indigo-700 break-words text-xs">
                      {a.from}
                    </td>
                    <td className="px-3 py-2 font-semibold text-emerald-700 break-words text-xs">
                      {a.to}
                    </td>
                    <td className="px-3 py-2 text-slate-900 text-xs font-semibold break-words">
                      {a.action}
                    </td>
                    <td className="px-3 py-2 text-slate-700 text-xs leading-relaxed break-words">
                      {a.how}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>,
      );
      n++;
    }

    // 13. Inline Views
    if (mod.inlineViews && mod.inlineViews.length > 0) {
      sections.push(
        <Section
          key="inlineviews"
          title={`${n}. Tiện ích Giao diện Thông minh (Inline UI)`}
          icon={<MousePointerClick size={16} className="text-violet-600" />}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {mod.inlineViews.map((v, i) => (
              <div
                key={i}
                className="bg-white border border-violet-200 shadow-xs rounded-lg p-3.5 hover:border-violet-400 transition-colors"
              >
                <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-violet-100">
                  <span className="font-bold text-sm text-violet-900">
                    {v.name}
                  </span>
                  <span className="text-[10px] uppercase font-bold tracking-wider bg-violet-100 text-violet-800 px-2 py-0.5 rounded-full">
                    {v.trigger}
                  </span>
                </div>
                <p className="text-xs text-slate-700 leading-relaxed">
                  {v.description}
                </p>
              </div>
            ))}
          </div>
        </Section>,
      );
      n++;
    }

    // 14. Collaborations
    if (mod.collaborations && mod.collaborations.length > 0) {
      sections.push(
        <Section
          key="collaborations"
          title={`${n}. Phối hợp Liên phòng ban & Nhật ký`}
          icon={<MessageSquarePlus size={16} className="text-sky-600" />}
        >
          <div className="grid grid-cols-1 gap-3">
            {mod.collaborations.map((c, i) => (
              <div
                key={i}
                className="flex gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3.5"
              >
                <div className="mt-0.5">
                  <MessageSquarePlus size={16} className="text-sky-600" />
                </div>
                <div>
                  <span className="font-bold text-sm text-slate-900 block mb-1">
                    {c.feature}
                  </span>
                  <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">
                    {c.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Section>,
      );
      n++;
    }

    // 15. Quick Actions
    if (mod.quickActions && mod.quickActions.length > 0) {
      sections.push(
        <Section
          key="quickactions"
          title={`${n}. Phím tắt & Thao tác Nhanh`}
          icon={<Zap size={16} className="text-yellow-600" />}
        >
          <div className="overflow-x-auto border border-slate-200 rounded-lg shadow-xs">
            <table className="w-full table-fixed text-sm">
              <colgroup>
                <col className="w-[26%]" />
                <col className="w-[20%]" />
                <col className="w-[54%]" />
              </colgroup>
              <thead>
                <tr className="bg-yellow-50/80 border-b border-yellow-100">
                  <th className="text-left px-3 py-2 font-bold text-slate-700">
                    Thao tác
                  </th>
                  <th className="text-left px-3 py-2 font-bold text-slate-700">
                    Cách kích hoạt
                  </th>
                  <th className="text-left px-3 py-2 font-bold text-slate-700">
                    Mô tả chi tiết
                  </th>
                </tr>
              </thead>
              <tbody>
                {mod.quickActions.map((q, i) => (
                  <tr
                    key={i}
                    className="border-t border-slate-100 bg-white hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-3 py-2 font-semibold text-slate-900 break-words text-xs">
                      {q.action}
                    </td>
                    <td className="px-3 py-2">
                      {q.shortcut && (
                        <span className="bg-slate-100 border border-slate-300 text-xs px-2 py-0.5 rounded font-mono text-slate-800">
                          {q.shortcut}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-700 text-xs leading-relaxed">
                      {q.description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>,
      );
      n++;
    }

    // 16. Hình ảnh Giao diện Minh họa Thực tế (Screenshots - NEW!)
    if (mod.screenshots && mod.screenshots.length > 0) {
      sections.push(
        <Section
          key="screenshots"
          title={`${n}. Hình ảnh Giao diện Thực tế trên Hệ thống`}
          icon={<ImageIcon size={16} className="text-blue-600" />}
        >
          <div
            data-testid={`screenshots-${mod.id}`}
            className="grid grid-cols-1 gap-5"
          >
            {mod.screenshots.map((shot, i) => (
              <figure
                key={i}
                className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs break-inside-avoid"
              >
                <div
                  onClick={() => setZoomedScreenshot(shot)}
                  className="relative group cursor-zoom-in bg-slate-100 border-b border-slate-200 overflow-hidden"
                >
                  <img
                    src={shot.src}
                    alt={shot.caption}
                    loading="eager"
                    className="w-full h-auto max-h-[460px] object-contain mx-auto transition-transform duration-200 group-hover:scale-[1.01]"
                  />
                  <div className="absolute top-2.5 right-2.5 bg-slate-900/75 text-white text-[11px] px-2.5 py-1 rounded-full flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity print:hidden">
                    <ZoomIn size={12} /> Phóng to ảnh
                  </div>
                </div>
                <figcaption className="px-4 py-2.5 bg-slate-50 text-xs text-slate-700 flex items-center gap-2">
                  <Badge
                    variant="secondary"
                    className="bg-blue-100 text-blue-800 font-bold text-[10px] shrink-0"
                  >
                    Hình {i + 1}
                  </Badge>
                  <span className="font-medium leading-relaxed">
                    {shot.caption}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </Section>,
      );
      n++;
    }

    // 17. Lưu ý quan trọng
    if (mod.notes && mod.notes.length > 0) {
      sections.push(
        <Section
          key="notes"
          title={`${n}. Lưu ý Quan trọng & Quy tắc Bảo vệ Dữ liệu`}
          icon={<AlertTriangle size={16} className="text-red-600" />}
        >
          <div className="bg-amber-50/90 border border-amber-200 rounded-lg p-4 space-y-2.5 shadow-xs">
            {mod.notes.map((note, i) => (
              <div
                key={i}
                className="flex items-start gap-2.5 text-xs md:text-sm text-amber-950 leading-relaxed"
              >
                <AlertTriangle
                  size={16}
                  className="text-amber-600 mt-0.5 shrink-0"
                />
                <span>{formatGuideText(note)}</span>
              </div>
            ))}
          </div>
        </Section>,
      );
      n++;
    }

    // 18. Biểu mẫu in ấn (Print Forms)
    if (mod.forms && mod.forms.length > 0) {
      sections.push(
        <Section
          key="forms"
          title={`${n}. Danh mục Biểu mẫu In ấn Chuẩn ISO 9001:2015`}
          icon={<Printer size={16} className="text-emerald-600" />}
        >
          <div className="overflow-x-auto border border-slate-200 rounded-lg shadow-xs">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-emerald-50 border-b border-emerald-100">
                  <th className="text-left px-3 py-2 font-bold text-emerald-900 w-1/3">
                    Mã Biểu mẫu ISO
                  </th>
                  <th className="text-left px-3 py-2 font-bold text-emerald-900">
                    Tên Biểu mẫu & Chứng từ In
                  </th>
                </tr>
              </thead>
              <tbody>
                {mod.forms.map((f, i) => (
                  <tr
                    key={i}
                    className="border-t border-slate-100 bg-white hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-3 py-2 text-xs text-emerald-800 font-mono font-bold">
                      {f.code}
                    </td>
                    <td className="px-3 py-2 text-slate-800 text-xs font-medium">
                      {f.name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>,
      );
    }

    return sections;
  };

  return (
    <div className="guide-root-container p-2 md:p-4 h-[calc(100vh-64px)] print:h-auto overflow-hidden print:overflow-visible flex flex-col print:block bg-slate-50/50 print:bg-white">
      {/* Print-specific CSS overrides so Full Manual prints all pages cleanly */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 12mm 10mm 12mm 10mm;
          }
          body, html, #root, main, .guide-root-container, .guide-scroll-wrapper, .guide-card-wrapper {
            display: block !important;
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
            position: static !important;
            background: #ffffff !important;
          }
          .guide-chapter-page-break {
            break-before: page;
            page-break-before: always;
          }
          .break-inside-avoid-page {
            break-inside: avoid;
          }
          nav, aside, header, .dock-nav, [data-testid="dock-navigation"], [data-testid="feedback-widget-trigger"], [data-testid="feedback-widget-modal"], [data-testid="floating-bulk-action-bar"], .safe-area-bottom {
            display: none !important;
          }
        }
      `}</style>

      {/* Top Action & Search Toolbar (Hidden when printing) */}
      <div className="mb-3 bg-white border border-slate-200 rounded-xl px-3 py-2.5 shadow-xs flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-2.5 shrink-0 print:hidden">
        {/* Left: Search Input + Chapter Count Badge */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="relative flex-1 max-w-md">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              type="search"
              data-testid="guide-search-input"
              placeholder="Tìm chương, kịch bản test CRUD, checklist Go-Live, nghiệp vụ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8 h-8 text-xs bg-slate-50 border-slate-200 focus:bg-white"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Badge
            variant="secondary"
            className="bg-indigo-50 text-indigo-700 border border-indigo-200 text-[11px] font-semibold px-2.5 py-0.5 shrink-0"
          >
            {filteredModules.length}/{modules.length} Chương
          </Badge>
        </div>

        {/* Right: View Mode Toggle + Print Buttons */}
        <div className="flex flex-wrap items-center gap-1.5">
          <div role="tablist" aria-label="Cách xem hướng dẫn" className="inline-flex items-end gap-1">
            <button
              type="button"
              data-testid="guide-view-single-btn"
              role="tab"
              aria-selected={viewMode === "single"}
              onClick={() => setViewMode("single")}
              className={emsTabClass(viewMode === "single", { size: "sm" })}
            >
              Xem Theo Chương
            </button>
            <button
              type="button"
              data-testid="guide-view-full-btn"
              role="tab"
              aria-selected={viewMode === "full"}
              onClick={() => setViewMode("full")}
              className={emsTabClass(viewMode === "full", { size: "sm" })}
            >
              Xem Toàn Bộ ({modules.length} Chương)
            </button>
          </div>

          <Button
            variant="outline"
            size="sm"
            data-testid="guide-print-chapter-btn"
            onClick={handlePrintCurrentChapter}
            className="h-8 text-xs gap-1.5 border-slate-200 text-slate-700 hover:bg-slate-100 cursor-pointer"
          >
            <Printer className="h-3.5 w-3.5 text-slate-500" />
            <span className="hidden sm:inline">In Chương Này</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            data-testid="guide-print-full-btn"
            onClick={handlePrintFullManual}
            className="h-8 text-xs gap-1.5 border-indigo-200 bg-indigo-50/60 text-indigo-700 hover:bg-indigo-100 font-semibold cursor-pointer"
          >
            <Printer className="h-3.5 w-3.5 text-indigo-600" />
            <span>In / Xuất PDF Toàn Bộ</span>
          </Button>

        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 flex-1 min-h-0 print:block">
        {/* Sidebar TOC (Hidden in print mode) */}
        <div className="md:col-span-3 flex-none flex flex-col gap-4 overflow-hidden h-48 md:h-full print:hidden">
          <Card className="flex-1 flex flex-col overflow-hidden border-slate-200 shadow-xs h-full rounded-xl border bg-white">
            <div className="space-y-1 p-2 bg-slate-50/50 md:bg-white h-full overflow-y-auto">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 px-2.5 py-2 border-b border-slate-100 mb-1 flex items-center justify-between">
                <span>Mục lục Cẩm nang</span>
                <span className="text-indigo-600 font-mono">
                  v{guideData.version}
                </span>
              </div>
              {filteredModules.map((mod) => {
                const Icon = iconMap[mod.icon as string] || BookOpen;
                const isActive =
                  viewMode === "single" && selectedModule?.id === mod.id;
                return (
                  <button
                    key={mod.id}
                    type="button"
                    data-testid={`guide-nav-${mod.id}`}
                    onClick={() => {
                      setSelectedModule(mod);
                      if (viewMode === "full") {
                        const el = document.getElementById(
                          `chapter-${mod.id}`,
                        );
                        el?.scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2 text-xs rounded-lg transition-all duration-150 group cursor-pointer ${
                      isActive
                        ? "bg-indigo-50 text-indigo-700 font-semibold shadow-2xs ring-1 ring-indigo-200"
                        : "hover:bg-slate-100 text-slate-700 hover:text-slate-900"
                    }`}
                  >
                    <div
                      className={`p-1.5 rounded-md ${
                        isActive
                          ? "bg-white shadow-2xs"
                          : "bg-slate-100/70 group-hover:bg-white"
                      }`}
                    >
                      <Icon
                        className={`h-3.5 w-3.5 shrink-0 ${
                          isActive
                            ? "text-indigo-600"
                            : "text-slate-500 group-hover:text-indigo-600"
                        }`}
                      />
                    </div>
                    <span className="flex-1 text-left line-clamp-2 leading-snug">
                      {mod.title}
                    </span>
                    {isActive && (
                      <ChevronRight className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Main Content Area */}
        <div className="md:col-span-9 flex-1 flex flex-col overflow-hidden h-full pb-0 md:pb-2 print:overflow-visible print:h-auto">
          <Card className="guide-card-wrapper flex-1 flex flex-col overflow-hidden border-slate-200 shadow-xs h-full rounded-xl border bg-white print:border-0 print:shadow-none">
            <CardContent className="p-0 flex-1 overflow-hidden relative bg-white print:overflow-visible">
              <div
                ref={scrollWrapperRef}
                className="guide-scroll-wrapper absolute inset-0 overflow-y-auto px-4 md:px-10 py-6 md:py-8 scroll-smooth print:static print:overflow-visible print:p-0"
              >
                <div className="max-w-5xl mx-auto pb-12 print:max-w-none print:pb-0">
                  {viewMode === "full" ? (
                    <div data-testid="guide-full-manual-container">
                      {/* ISO Cover Banner for Full Manual & PDF Export */}
                      <div
                        data-testid="guide-full-cover"
                        className="mb-10 p-6 md:p-8 rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/80 via-white to-emerald-50/50 shadow-xs"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                          <Badge className="bg-indigo-700 text-white font-bold px-3 py-1 text-xs uppercase tracking-wider">
                            Tài Liệu Chuẩn Hóa Vận Hành ISO 9001:2015
                          </Badge>
                          <span className="text-xs font-mono font-bold text-slate-600 bg-white px-3 py-1 rounded-md border border-slate-200">
                            Phiên bản: {guideData.version} • Cập nhật:{" "}
                            {guideData.last_updated}
                          </span>
                        </div>
                        <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight leading-tight mb-3">
                          CẨM NANG HƯỚNG DẪN SỬ DỤNG & NGHIỆM THU GO-LIVE TOÀN
                          HỆ THỐNG
                        </h1>
                        <p className="text-sm text-slate-700 leading-relaxed mb-5">
                          Tài liệu hướng dẫn chi tiết toàn trình các phân hệ
                          nghiệp vụ Bao Bì Giấy Offset (Báo giá → Đơn hàng SO →
                          Bình bài Gang-Run → Lệnh sản xuất Đơn/Ghép/Tách → Cấp
                          giấy & Thu hồi mực Pantone → Khuôn bế & Bản kẽm CTP →
                          Điều độ lịch máy APS & Xưởng MES → KCS IQC/IPQC/FQC &
                          In tem thùng → Kho vận WMS → Tài chính Công nợ), kèm{" "}
                          <strong>
                            Kịch bản Test CRUD theo 18 Vai trò (Roles)
                          </strong>
                          , <strong>Checklist Tiêu chuẩn Đánh giá Go-Live</strong>{" "}
                          và{" "}
                          <strong>
                            Quy trình Gửi Phản hồi / Báo lỗi trực tiếp cho Đội
                            Kỹ thuật ngay trên App
                          </strong>
                          .
                        </p>

                        {/* Table of Contents (Mục lục toàn bộ chương) */}
                        <div
                          data-testid="guide-full-toc"
                          className="bg-white/90 border border-slate-200 rounded-xl p-4"
                        >
                          <h2 className="text-xs font-bold uppercase tracking-wider text-indigo-800 mb-3 flex items-center gap-1.5">
                            <BookOpen size={14} /> Mục Lục Toàn Bộ {modules.length} Chương
                            Nghiệp Vụ & Kiểm Thử Go-Live
                          </h2>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {filteredModules.map((m) => (
                              <a
                                key={m.id}
                                href={`#chapter-${m.id}`}
                                className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg border border-slate-100 hover:border-indigo-200 hover:bg-indigo-50/40 text-xs text-slate-800 font-medium transition-colors"
                              >
                                <span className="truncate">{m.title}</span>
                                {m.isoRef && (
                                  <span className="font-mono text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded shrink-0">
                                    {m.isoRef}
                                  </span>
                                )}
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Render All 16 Chapters Sequentially */}
                      <div className="space-y-14">
                        {filteredModules.map((mod, idx) => (
                          <article
                            key={mod.id}
                            id={`chapter-${mod.id}`}
                            data-testid={`guide-chapter-${mod.id}`}
                            className={
                              idx > 0
                                ? "guide-chapter-page-break pt-8 border-t-2 border-slate-200"
                                : ""
                            }
                          >
                            <div className="mb-6 pb-4 border-b border-slate-200">
                              <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-2">
                                <h2 className="text-xl md:text-2xl font-extrabold text-slate-900 tracking-tight">
                                  {mod.title}
                                </h2>
                                {mod.isoRef && (
                                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-700/10 shrink-0">
                                    <BookOpen
                                      size={13}
                                      className="text-indigo-600"
                                    />
                                    ISO: {mod.isoRef}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="space-y-2">
                              {renderModuleDoc(mod)}
                            </div>
                          </article>
                        ))}
                      </div>
                    </div>
                  ) : selectedModule ? (
                    <div data-testid={`guide-single-${selectedModule.id}`}>
                      {/* Single Chapter Header */}
                      <div className="mb-8 pb-5 border-b border-slate-200">
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-3">
                          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight leading-tight">
                            {selectedModule.title}
                          </h1>
                          <div className="shrink-0">
                            {selectedModule.isoRef && (
                              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-700/10 shadow-2xs uppercase tracking-wider">
                                <BookOpen
                                  size={14}
                                  className="text-indigo-600"
                                />
                                ISO: {selectedModule.isoRef}
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="text-slate-600 text-xs md:text-sm leading-relaxed">
                          Cẩm nang hướng dẫn vận hành tiêu chuẩn, kịch bản kiểm
                          thử CRUD theo phân quyền và tiêu chuẩn nghiệm thu
                          Go-Live.
                        </p>
                      </div>

                      {/* Content Sections */}
                      <div className="space-y-2">
                        {renderModuleDoc(selectedModule)}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-64 text-slate-400 space-y-4">
                      <BookOpen size={48} className="text-slate-200" />
                      <p className="text-sm font-medium">
                        Không tìm thấy chương hướng dẫn phù hợp với từ khóa.
                      </p>
                    </div>
                  )}

                  {/* ISO Footer */}
                  <div className="border-t border-slate-200 pt-6 mt-12 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={16} className="text-emerald-600" />
                      <span className="font-semibold text-slate-700">
                        Tài liệu Cẩm nang Vận hành & Nghiệm thu Go-Live chuẩn
                        ISO 9001:2015
                      </span>
                    </div>
                    <div className="text-right">
                      <p>
                        Phiên bản tài liệu:{" "}
                        <span className="font-bold text-slate-800">
                          {guideData.version}
                        </span>{" "}
                        • Cập nhật lần cuối:{" "}
                        <span className="font-bold text-slate-800">
                          {guideData.last_updated}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Lightbox Modal when user clicks a screenshot to zoom */}
      {zoomedScreenshot && (
        <div
          onClick={() => setZoomedScreenshot(null)}
          className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-4 print:hidden"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-6xl w-full max-h-[92vh] flex flex-col overflow-hidden shadow-2xl border border-slate-200"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
              <span className="text-xs md:text-sm font-bold text-slate-800 truncate">
                {zoomedScreenshot.caption}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setZoomedScreenshot(null)}
                className="h-7 w-7 rounded-full"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4 overflow-auto bg-slate-100 flex-1 flex items-center justify-center">
              <img
                src={zoomedScreenshot.src}
                alt={zoomedScreenshot.caption}
                className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-md"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
